import { thunkGetAppConfig } from 'app/appSlice';
import { AgencyConstants } from 'features/Agency/agencyHelper';
import KT01BaseLayout from 'general/components/BaseLayout/KT01BaseLayout';
import KTFormInput, {
  KTFormInputType,
} from 'general/components/OtherKeenComponents/Forms/KTFormInput';
import KTTooltip from 'general/components/OtherKeenComponents/KTTooltip';
import AppColors from 'general/constants/AppColors';
import miscHelper from 'general/helpers/miscHelper';
import ToastHelper from 'general/helpers/ToastHelper';
import UIHelper from 'general/helpers/UIHelper';
import useAppConfig from 'hooks/useAppConfig';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

const commissionSharingRows = [
  { key: 'evgOperation', name: 'EVG vận hành' },
  { key: 'premiseReferral', name: 'Giới thiệu MB' },
  { key: 'investorReferral', name: 'Giới thiệu CĐT' },
  { key: 'landlord', name: 'MB' },
];

const parseCurrencyNumber = (value, fallback = 0) => {
  if (_.isNumber(value)) return Number.isFinite(value) ? value : fallback;
  if (_.isNil(value)) return fallback;
  const normalized = String(value).replace(/[^\d-]/g, '');
  if (!normalized || normalized === '-') return fallback;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const defaultCommissionSharingTables = [
  {
    key: 'MOTORBIKE',
    title: 'Chính sách chia sẻ giá điện trạm sạc Xe máy',
    columns: [
      {
        key: 'PRICE_0',
        price: 0,
        values: {
          electricityCost: 0,
          evgOperation: 0,
          premiseReferral: 0,
          investorReferral: 0,
          landlord: 0,
          investor: 0,
        },
      },
    ],
  },
  {
    key: 'CAR',
    title: 'Chính sách chia sẻ giá điện trạm sạc Xe ô tô',
    columns: [
      {
        key: 'PRICE_0',
        price: 0,
        values: {
          electricityCost: 0,
          evgOperation: 0,
          premiseReferral: 0,
          investorReferral: 0,
          landlord: 0,
          investor: 0,
        },
      },
    ],
  },
];

const cloneCommissionSharingTables = (tables = defaultCommissionSharingTables) =>
  tables.map((table) => ({
    ...table,
    columns: table.columns.map((column) => ({
      ...column,
      values: { ...column.values },
    })),
  }));

const normalizeCommissionSharingTables = (parsedConfig) => {
  const savedTables = _.isArray(parsedConfig?.tables) ? parsedConfig.tables : [];
  if (!savedTables.length) return cloneCommissionSharingTables();

  return defaultCommissionSharingTables.map((defaultTable) => {
    const savedTable = savedTables.find((item) => item.key === defaultTable.key);
    return {
      ...defaultTable,
      columns: defaultTable.columns.map((defaultColumn) => {
        const savedColumn = savedTable?.columns?.find((item) => item.key === defaultColumn.key) ?? savedTable?.columns?.[0];
        return {
          ...defaultColumn,
          price: parseCurrencyNumber(savedColumn?.price, defaultColumn.price),
          values: commissionSharingRows.reduce((result, row) => ({
            ...result,
            [row.key]: parseCurrencyNumber(savedColumn?.values?.[row.key], defaultColumn.values[row.key] ?? 0),
          }), {
            electricityCost: parseCurrencyNumber(savedColumn?.values?.electricityCost, defaultColumn.values.electricityCost ?? 0),
            investor: parseCurrencyNumber(savedColumn?.values?.investor, defaultColumn.values.investor ?? 0),
          }),
        };
      }),
    };
  });
};

const parseCommissionSharingTables = (value) => {
  if (!value) return cloneCommissionSharingTables();
  try {
    const parsed = JSON.parse(value);
    return normalizeCommissionSharingTables(parsed);
  } catch (error) {
    return cloneCommissionSharingTables();
  }
};

const parseCommissionGrounds = (value) => {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    return _.isArray(parsed)
      ? parsed
        .filter(item => String(item?.name ?? '').trim() !== '')
        .map(item => ({
          key: item.key,
          name: String(item.name ?? '').trim(),
          value: item?.value ?? 0,
          car: item?.car ?? 0,
          bike: item?.bike ?? 0,
          show: Boolean(item.show),
        }))
      : [];
  } catch (error) {
    return [];
  }
};

const hasGroundContent = (item) =>
  Boolean(String(item?.name ?? '').trim())
  || parseCurrencyNumber(item?.value) > 0
  || parseCurrencyNumber(item?.car) > 0
  || parseCurrencyNumber(item?.bike) > 0;

const isGroundRowValid = (item) => {
  if (!hasGroundContent(item)) return true;
  if (!String(item?.name ?? '').trim()) return false;
  if (item?.show) {
    return parseCurrencyNumber(item?.car) > 0 && parseCurrencyNumber(item?.bike) > 0;
  }
  return parseCurrencyNumber(item?.value) > 0;
};

function AgencyConfigCommissionScreen(props) {
  // MARK: ---- PROPERTIES ----
  const dispatch = useDispatch();
  const [editMode, setEditMode] = useState(false);
  const [commissionSharingTables, setCommissionSharingTables] = useState(cloneCommissionSharingTables());
  const [commissionGrounds, setCommissionGrounds] = useState([]);
  const appConfig = useAppConfig();
  useEffect(() => {
    if (appConfig.affiliateCommissionServices) {
      setCommissionSharingTables(parseCommissionSharingTables(appConfig.affiliateCommissionServices));
    }

    setCommissionGrounds(parseCommissionGrounds(appConfig.commissionRateLandlord));
  }, [appConfig]);
  const [loading, setLoading] = useState(false);

  // MARK: ---- FUNCTIONS ----
  const handleSave = async () => {
    // validate du lieu
    const hasInvalidSharingTable = commissionSharingTables.some((table) =>
      !String(table.title ?? '').trim() ||
      table.columns.some((column) =>
        commissionSharingRows.some((row) =>
          Number.isNaN(parseCurrencyNumber(column.values?.[row.key], NaN)) ||
          parseCurrencyNumber(column.values?.[row.key], NaN) < 0
        )
      )
    );
    if (hasInvalidSharingTable) {
      ToastHelper.showError('Các giá trị chia sẻ hoa hồng phải là số không âm');
      return;
    }
    const hasInvalidGround = commissionGrounds.some((item) => !isGroundRowValid(item));
    if (hasInvalidGround) {
      ToastHelper.showError('Hoa hồng giới thiệu mặt bằng chỉ bắt buộc khi có nhập loại mặt bằng');
      return;
    }
    const filteredCommissionGrounds = commissionGrounds.filter((item) => String(item.name ?? '').trim() !== '');

    const paramCommissionServices = {
      key: AgencyConstants.ConfigKey.AFFILIATE_COMMISSION_SERVICES,
      value: JSON.stringify(
        {
          schemaVersion: 'ENERGY_SHARING_V1',
          tables: commissionSharingTables.map((table) => ({
            key: table.key,
            title: table.title,
            columns: table.columns.map((column) => ({
              key: column.key,
              price: parseCurrencyNumber(column.price),
              values: commissionSharingRows.reduce((result, row) => ({
                ...result,
                [row.key]: parseCurrencyNumber(column.values?.[row.key]),
              }), {
                electricityCost: parseCurrencyNumber(column.values?.electricityCost),
                investor: parseCurrencyNumber(column.values?.investor),
              }),
            })),
          })),
        }
      ),
    };
    // ty le hoa hong gioi thieu mat bang
    const paramCommissionLandlord = {
      key: AgencyConstants.ConfigKey.COMMISSION_RATE_LANDLORD,
      value: JSON.stringify(
        filteredCommissionGrounds?.map((item, index) => {
          const result = {
            key: item.key ?? `loai_${index + 1}`,
            name: String(item.name ?? '').trim(),
            show: item.show,
          };

          const value = !item.show ? parseCurrencyNumber(item.value) : 0;
          const car = item.show ? parseCurrencyNumber(item.car) : 0;
          const bike = item.show ? parseCurrencyNumber(item.bike) : 0;

          if (!item?.show) result.value = value;
          if (item?.show) result.car = car;
          if (item?.show) result.bike = bike;

          return result;
        }) || []
      ),
    };
    setLoading(true);
    try {
      const savedServices = await miscHelper.callApiUpdateConfig(paramCommissionServices);
      const savedLandlord = await miscHelper.callApiUpdateConfig(paramCommissionLandlord);
      if (!savedServices || !savedLandlord) throw new Error('Không thể lưu cấu hình');

      ToastHelper.showSuccess('Lưu cấu hình hoa hồng thành công');
      setEditMode(false);
      dispatch(thunkGetAppConfig());
    } catch (error) {
      ToastHelper.showError(error?.response?.data?.reason || 'Lưu cấu hình hoa hồng thất bại');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setEditMode(false);
    dispatch(thunkGetAppConfig());
  };

  const handleUpdateSharingValue = (tableIndex, columnIndex, rowKey, value) => {
    setCommissionSharingTables((prev) => {
      const nextTables = cloneCommissionSharingTables(prev);
      nextTables[tableIndex].columns[columnIndex].values[rowKey] = value;
      return nextTables;
    });
  };

  return (
    <KT01BaseLayout isFullScreen={true}>
      <div className="container-fluid p-4">
        {UIHelper.buildCard(
          <div className="px-4 pt-4">
            {/* header */}
            <div className="d-flex align-items-center justify-content-between">
              <span style={UIHelper.buildFontStyle('600', '18px', AppColors.gray[800])}>
                Cấu hình hoa hồng
              </span>
              {editMode && (
                <div className='d-flex gap-3'>
                  {UIHelper.buildNegativeButton('Hủy bỏ', handleCancel, null)}
                  {UIHelper.buildGreenButton('Lưu thay đổi', handleSave, null, false, loading)}
                </div>
              )}
              {!editMode && UIHelper.buildBlueButton('Chỉnh sửa', () => setEditMode(true), null)}
            </div>
            {/* body */}
            <div>
              <div className="pt-2 pb-4" style={{ borderBottom: `1px dashed ${AppColors.gray[300]}` }}>
                <div className="d-flex align-items-center justify-content-between">
                  <span style={UIHelper.buildFontStyle('700', '14px', AppColors.gray[800])}>
                    Dịch vụ hoa hồng bán hàng
                  </span>
                </div>
                <div className="mt-3 d-flex flex-wrap gap-4 align-items-stretch">
                  {commissionSharingTables.map((table, tableIndex) => (
                    <div
                      key={table.key}
                      style={{
                        flex: '1 1 500px',
                        border: `1px solid ${AppColors.gray[300]}`,
                        borderRadius: 8,
                        overflow: 'hidden',
                        background: '#ffffff',
                      }}
                    >
                      <div
                        className="text-center py-2"
                        style={{
                          background: '#f8fafc',
                          fontWeight: 700,
                          color: AppColors.gray[900],
                        }}
                      >
                        {table.title}
                      </div>
                      <div className="table-responsive">
                        <table className="table mb-0 table-bordered">
                          <thead>
                            <tr>
                              <th
                                style={{
                                  width: 220,
                                  background: '#f8fafc',
                                  color: AppColors.gray[900],
                                  verticalAlign: 'middle',
                                }}
                              >
                                Khoản chia
                              </th>
                              {table.columns.map((column) => (
                                <th key={column.key} style={{ background: '#f8fafc', minWidth: 180 }}>
                                  Mức cố định
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {commissionSharingRows.map((row) => (
                              <tr key={row.key}>
                                <td
                                  style={{
                                    fontWeight: 600,
                                    background: '#ffffff',
                                    color: AppColors.gray[800],
                                    verticalAlign: 'middle',
                                  }}
                                >
                                  {row.name}
                                </td>
                                {table.columns.map((column, columnIndex) => (
                                  <td key={`${column.key}${row.key}`}>
                                    <KTFormInput
                                      name={`sharingValue${tableIndex}${columnIndex}${row.key}`}
                                      value={column.values?.[row.key] ?? 0}
                                      type={KTFormInputType.currency}
                                      onChange={(value) => handleUpdateSharingValue(tableIndex, columnIndex, row.key, value)}
                                      disabled={!editMode}
                                      unit="VNĐ"
                                      showUnit={false}
                                    />
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* hoa hong gioi thieu mat bang */}
              <div className="py-4">
                <span style={UIHelper.buildFontStyle('700', '14px', AppColors.gray[800])}>
                  Hoa hồng giới thiệu mặt bằng
                </span>
                <div className="mt-2 d-flex flex-column gap-3 mb-4">
                  {commissionGrounds?.map((item, index) => {
                    const shouldValidateGround = editMode && hasGroundContent(item);
                    return (
                    <div
                      key={index}
                      className={`d-flex w-100 align-items-end gap-3`}
                    >
                      <div className='flex-grow-1'>
                        {UIHelper.buildFormLabel('Loại mặt bằng')}
                        <KTFormInput
                          name={`commissionGroundName${index}`}
                          value={item.name}
                          onChange={(value) => {
                            const newCommissionGrounds = [...commissionGrounds];
                            newCommissionGrounds[index].name = value;
                            setCommissionGrounds(newCommissionGrounds);
                          }}
                          placeholder="Nhập tên loại mặt bằng"
                          disabled={!editMode}
                        />
                      </div>
                      {!item?.show ? (
                        <div style={{ width: '200px' }}>
                          {UIHelper.buildFormLabel('Mức hoa hồng')}
                          <KTFormInput
                            name={`commissionGroundValue${index}`}
                            value={item.value}
                            unit="VNĐ"
                            type={KTFormInputType.currency}
                            onChange={(value) => {
                              const newCommissionGrounds = [...commissionGrounds];
                              newCommissionGrounds[index].value = value;
                              setCommissionGrounds(newCommissionGrounds);
                            }}
                            isValid={!shouldValidateGround || parseCurrencyNumber(item.value) > 0}
                            disabled={!editMode}
                          />
                        </div>
                      ) : (
                        <>
                          <div style={{ width: '200px' }}>
                            {UIHelper.buildFormLabel('Mức hoa hồng ô tô')}
                            <KTFormInput
                              name={'commissionGroundValueByCar' + index}
                              value={item.car}
                              unit="VNĐ"
                              type={KTFormInputType.currency}
                              onChange={(value) => {
                                const newCommissionGrounds = [...commissionGrounds];
                                newCommissionGrounds[index].car = value;
                                setCommissionGrounds(newCommissionGrounds);
                              }}
                              isValid={!shouldValidateGround || parseCurrencyNumber(item.car) > 0}
                              disabled={!editMode}
                            />
                          </div>
                          <div style={{ width: '200px' }}>
                            {UIHelper.buildFormLabel('Mức hoa hồng xe máy')}
                            <KTFormInput
                              name={'commissionGroundValueByBike' + index}
                              value={item.bike}
                              unit="VNĐ"
                              type={KTFormInputType.currency}
                              onChange={(value) => {
                                const newCommissionGrounds = [...commissionGrounds];
                                newCommissionGrounds[index].bike = value;
                                setCommissionGrounds(newCommissionGrounds);
                              }}
                              isValid={!shouldValidateGround || parseCurrencyNumber(item.bike) > 0}
                              disabled={!editMode}
                            />
                          </div>
                        </>
                      )}

                      <div style={{ width: '80px' }}>
                        {index === 0
                          ? <div className='d-flex gap-1'>
                            {UIHelper.buildFormLabel('Thao tác', false, false)}
                            <KTTooltip
                              text={`<div><span>Bật/tắt theo 2 loại phương tiện ô tô/xe máy</span></div>`}
                              textHeader="Hoa hồng theo phương tiện"
                              isHtmlContent
                            >
                              <i className="fa-regular fa-circle-info" style={{ color: AppColors.info }} />
                            </KTTooltip>
                          </div>
                          : UIHelper.buildFormLabel('')}
                        <div className='d-flex gap-2 align-items-center'>
                          {UIHelper.buildToggleSwitch(
                            item.show,
                            () => {
                              if (!editMode) return;
                              setCommissionGrounds(prev => {
                                const newCommissionGrounds = [...prev];
                                if (newCommissionGrounds[index]) {
                                  newCommissionGrounds[index].show = !prev[index].show;
                                }
                                return newCommissionGrounds;
                              });
                            }
                          )}
                          <span
                            className="btn btn-sm btn-clean btn-icon p-0"
                            onClick={() => {
                              if (!editMode) return;
                              const newCommissionGrounds = [...commissionGrounds];
                              newCommissionGrounds.splice(index, 1);
                              setCommissionGrounds(newCommissionGrounds);
                            }}>
                            <i className="fa-regular fa-trash-can cursor-pointer" style={{ color: AppColors.gray[600] }} />
                          </span>
                        </div>
                      </div>
                    </div>
                    );
                  })}
                </div>
                <div className="d-flex">
                  {UIHelper.buildNegativeButton(
                    'Thêm',
                    () => {
                      if (!editMode) return;
                      setCommissionGrounds([...commissionGrounds, { name: '', value: 0, bike: 0, car: 0, show: 0 }]);
                    },
                    <i className="fa-regular fa-plus" />
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </KT01BaseLayout>
  );
}

export default AgencyConfigCommissionScreen;
